import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import { findInvoicingMirror } from "../../../../repositories/qerpapi/erp/relatorio/EspelhoFaturamentoRepository";
import { findClientsV3 } from "../../../../repositories/qerpapi/v3/ClienteRepository";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { findReceiptSituations } from "../../../../repositories/qerpapi/v3/SituacoesRepository";
import { notify } from "../../../../utils/notify";
import { periods } from "../../../financeiro/constants";
import {
  contractColumns,
  initialTaxFilter,
  invoiceMirrorGroupment,
  invoiceMirrorSituations,
  receivablesColumns,
  taxNotesColumns,
} from "../constants";

const EspelhoFaturamentoContext = createContext();

const EspelhoFaturamentoContextProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...initialTaxFilter });
  const [filterPeriod, setFilterPeriod] = useState({
    ...initialTaxFilter.searchDate,
  });

  const [columns, setColumns] = useState([
    ...contractColumns,
    ...receivablesColumns,
    ...taxNotesColumns,
  ]);

  const [isMainFetchEnabled, setMainFetchEnabled] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isIndeterminate, setIndeterminate] = useState(false);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedClients, setCachedClients] = useState([]);
  const [cachedSituations, setCachedSituations] = useState([]);
  const [cachedInvoicing, setCachedInvoicing] = useState({
    data: [],
    period: {},
    isGrouped: false,
  });

  const [quickSituations, setQuickSituations] = useState(
    invoiceMirrorSituations
  );

  // ----------------------------------------------------------------------------

  const handleCheckAll = useCallback(
    (check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        situation.checked = check;
        return situation;
      });
      setIndeterminate(
        checekdSituations.some((situation) => situation.checked)
      );
      setCachedSituations(checekdSituations);
    },
    [cachedSituations]
  );

  const handleCheckSituations = useCallback(
    (checkedSituation, check) => {
      const checekdSituations = cachedSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      const isAllSituationsCheckeds = checekdSituations.every(
        (checkedSituation) => checkedSituation.checked
      );
      const isSomeSituationsCheckeds = checekdSituations.some(
        (checkedSituation) => checkedSituation.checked
      );

      setCachedSituations(checekdSituations);

      if (isAllSituationsCheckeds) {
        handleCheckAll(check);
        return;
      }

      setIndeterminate(isSomeSituationsCheckeds);
    },
    [cachedSituations, handleCheckAll]
  );

  const handleCheckQuickSituations = useCallback(
    (checkedSituation, check) => {
      const checkedSituations = quickSituations.map((situation) => {
        if (checkedSituation.id === situation.id) {
          situation.checked = check;
        }
        return situation;
      });

      setQuickSituations(checkedSituations);
    },
    [quickSituations]
  );

  const clearAllSituationsFilters = useCallback(() => {
    handleCheckAll(false);
    const checkedSituations = quickSituations.map((situation) => {
      situation.checked = false;

      return situation;
    });

    setQuickSituations(checkedSituations);
    setQuickSituations(checkedSituations);
  }, [handleCheckAll, quickSituations]);

  // ----------------------------------------------------------------------------

  const setInvoicingMirrorData = useCallback(
    (data) => setCachedInvoicing((prev) => ({ ...prev, ...data })),
    []
  );

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback(
    (selectedCompany) =>
      setFilters((prev) => ({ ...prev, multicompany: selectedCompany })),
    []
  );

  const setSelectedCustomer = useCallback(
    (selectedCustomer) =>
      setFilters((prev) => ({ ...prev, customer: selectedCustomer })),
    []
  );

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setGroupBy = useCallback((newValue) => {
    setFilters((prev) => {
      return {
        ...prev,
        group: newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setCustomSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setMulticompany = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, multicompany: newValue }));
  }, []);

  const setContractNumber = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, contractNumber: newValue }));
  }, []);

  const setContractStatus = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, contractStatus: newValue }));
  }, []);

  const setModality = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, contractModality: newValue }));
  }, []);

  const setDateFilter = useCallback((newValue) => {
    setFilters((prev) => ({ ...prev, dateFilter: newValue }));
  }, []);

  const setMultipleSort = useCallback((sortValues) => {
    setFilters((prev) => ({ ...prev, multipleSort: sortValues }));
  }, []);

  const setSituations = useCallback(() => {
    const checkedSituations = cachedSituations
      .filter((situation) => situation.checked)
      .map((situation) => situation.id);

    const checkedQuickSituations = quickSituations
      .filter((quickSituation) => quickSituation.checked)
      .map((quickSituation) => quickSituation.id);

    setFilters((prev) => ({
      ...prev,
      accountReceivableStatus: checkedSituations,
      accountReceivableQuickStatus: checkedQuickSituations,
    }));
  }, [cachedSituations, quickSituations]);

  // ----------------------------------------------------------------------------

  const formatSortedLabel = useCallback((sortFilter) => {
    if (!sortFilter) return "Sem ordenação";

    const sort = JSON.parse(sortFilter);
    if (!sort.length) return "Data Emissão";

    const sortedLabels = taxNotesColumns
      .filter((column) => sort.find((sorted) => sorted.column === column.id))
      .map((item) => item.label);
    return sortedLabels.join(", ");
  }, []);

  const formatGroupedLabel = useCallback((groupFilter) => {
    if (!groupFilter) return "Sem agrupamento";

    const groups = Object.entries(invoiceMirrorGroupment);
    const [groupLabel] = groups.find(([_, value]) => value === groupFilter);
    return groupLabel;
  }, []);

  const handleSetSort = useCallback(
    (sorted) => {
      const sortedColumns = JSON.parse(filters.multipleSort ?? "[]");
      const sortedIndex = sortedColumns.findIndex(
        (value) => sorted.column === value.column
      );
      if (sorted.direction === "disabled" && sortedIndex >= 0) {
        sortedColumns.splice(sortedIndex, 1);
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      if (sortedIndex >= 0) {
        sortedColumns[sortedIndex] = sorted;
        return setMultipleSort(JSON.stringify(sortedColumns));
      }

      sortedColumns.push(sorted);
      setMultipleSort(JSON.stringify(sortedColumns));
    },
    [filters.multipleSort, setMultipleSort]
  );

  const handleSort = useCallback(
    (sorted) => {
      const sortedColumns = columns.map((column) => {
        if (column.id === sorted.id) {
          if (column.sorting === "disabled") {
            handleSetSort({ column: column.id, direction: "asc" });
            return {
              ...column,
              sorting: "asc",
            };
          }

          if (column.sorting === "asc") {
            handleSetSort({ column: column.id, direction: "desc" });
            return {
              ...column,
              sorting: "desc",
            };
          }

          if (column.sorting === "desc") {
            handleSetSort({ column: column.id, direction: "disabled" });
            return {
              ...column,
              sorting: "disabled",
            };
          }
        }
        return column;
      });

      setColumns(sortedColumns);
    },
    [columns, handleSetSort]
  );

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setCustomSearchDate(periodDates);
      setInvoicingMirrorData({ period: periodDates });
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setInvoicingMirrorData, setCustomDatePeriod, setCustomSearchDate]
  );

  useEffect(() => {
    setCustomSearchDate(filterPeriod);
  }, [filterPeriod, setCustomSearchDate]);

  // ----------------------------------------------------------------------------

  const handleMainData = useCallback(
    async (res) => {
      const data = res?.data;
      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
        return;
      }

      setInvoicingMirrorData({
        data: data.movements,
        isGrouped: !!filters.group,
      });
      applyPeriod(data?.searchPeriod);
    },
    [applyPeriod, filters.group, setInvoicingMirrorData]
  );

  const { isLoading, isRefetching, refetch } = useQuery(
    "espelho-faturamento",
    () => findInvoicingMirror(filters),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
      onSuccess: handleMainData,
    }
  );

  // ----------------------------------------------------------------------------

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnabled(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  const { isLoading: isCompanyLoading } = useQuery(
    "empresas",
    findCompaniesV3,
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: handleCompaniesData,
    }
  );

  // ----------------------------------------------------------------------------

  const handleClientsData = useCallback(async (clients) => {
    const data = clients?.data ?? [];

    if (!clients.success) return notify(clients.message, true, "error");

    const formatedClients = data.map((client) => ({
      description: client.razao,
      value: client.id,
    }));

    setCachedClients(formatedClients);
  }, []);

  const { isLoading: isClientLoading } = useQuery("clientes", findClientsV3, {
    enabled: !!sessionStorage.getItem("gfAccessToken"),
    onSuccess: handleClientsData,
  });

  // ----------------------------------------------------------------------------

  const handleRemoveChip = useCallback(
    (checkedSituation) => {
      const isQuickSituation = quickSituations.some(
        (quick) => quick.id === checkedSituation.id
      );
      const isSituation = cachedSituations.some(
        (situation) => situation.id === checkedSituation.id
      );

      if (isQuickSituation) {
        handleCheckQuickSituations(checkedSituation, false);
      }

      if (isSituation) {
        handleCheckSituations(checkedSituation, false);
      }

      setSituations();
    },
    [
      cachedSituations,
      handleCheckQuickSituations,
      handleCheckSituations,
      quickSituations,
      setSituations,
    ]
  );

  const { isLoading: isSituationsLoading } = useQuery(
    "invoice-situations",
    async () =>
      await findReceiptSituations(sessionStorage.getItem("empresa_cnpj")),
    {
      enabled:
        !!sessionStorage.getItem("gfAccessToken") &&
        !!sessionStorage.getItem("empresa_cnpj"),
      onSuccess: (situations) => handleSituationsData(situations),
    }
  );

  const handleSituationsData = useCallback(async (situations) => {
    const data = situations?.data ?? [];

    if (!situations.success) notify(situations.message, true, "error");

    if (data.length) {
      const formattedSituations = data
        .sort((situationA, situationB) =>
          situationA.descricao < situationB.descricao ? -1 : 1
        )
        .map((dataItem) => {
          return {
            id: dataItem.codigo,
            desc: dataItem.descricao,
            checked: false,
          };
        });

      setCachedSituations(formattedSituations);
    }
  }, []);

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      filterPeriod,
      columns,

      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setContractNumber,
      setContractStatus,
      setModality,
      setDateFilter,
      setGroupBy,

      setMultipleSort,

      setCustomDatePeriod,

      handleSort,
      formatSortedLabel,
      formatGroupedLabel,

      cachedInvoicing,
      cachedCompanies,
      cachedClients,
      cachedSituations,

      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,

      refetch,

      isDrawerOpen,
      setDrawerOpen,

      quickSituations,
      setQuickSituations,

      isIndeterminate,

      handleCheckSituations,
      handleCheckQuickSituations,
      clearAllSituationsFilters,
      setSituations,
      handleRemoveChip,
      handleCheckAll,
      isSituationsLoading,
    }),
    [
      filters,
      filterPeriod,
      columns,
      setSelectedCompany,
      setSelectedCustomer,
      setPeriod,
      setMulticompany,
      setContractNumber,
      setContractStatus,
      setModality,
      setDateFilter,
      setGroupBy,
      setMultipleSort,
      setCustomDatePeriod,
      handleSort,
      formatSortedLabel,
      formatGroupedLabel,
      cachedInvoicing,
      cachedCompanies,
      cachedClients,
      cachedSituations,
      isLoading,
      isRefetching,
      isCompanyLoading,
      isClientLoading,
      refetch,
      isDrawerOpen,
      quickSituations,
      isIndeterminate,
      handleCheckSituations,
      handleCheckQuickSituations,
      clearAllSituationsFilters,
      setSituations,
      handleRemoveChip,
      handleCheckAll,
      isSituationsLoading,
    ]
  );

  return (
    <EspelhoFaturamentoContext.Provider value={contextValue}>
      {children}
    </EspelhoFaturamentoContext.Provider>
  );
};

const useEspelhoFaturamentoService = () => {
  const context = useContext(EspelhoFaturamentoContext);

  if (!context)
    throw new Error(
      "useEspelhoFaturamentoService must be used with EspelhoFaturamentoContextProvider"
    );

  return context;
};

export { EspelhoFaturamentoContextProvider, useEspelhoFaturamentoService };
